import {
    InnerLayout,
    ZonesContent,
    ZonesFilter,
    ZonesHeader,
} from "components";

import React, {useCallback, useEffect, useRef, useState} from 'react';
import { Autocomplete, GoogleMap, Polygon, useJsApiLoader } from '@react-google-maps/api';
import DraftZonePath from "./draft-zone-path";
import deleteIcon from '../../assets/images/remove.png';
import {MAP_API_KEY} from "../../utils/constants";
import {usePlacesLibrary} from "../../utils/use-maps-libraries";
import {breadcrumbIcon, plusIcon} from "../../svgs";
import {Breadcrumb, Button, Spin, Grid} from "antd";
import {setCoordinates, setModalName} from "../../store/actions";
import {useDispatch} from "react-redux";
import {useHistory} from "react-router-dom";

import "styles/forms/add-zone.scss";

const libraries: ("places" | "drawing" | "geometry" | "localContext" | "visualization")[] = ["places"];
const { useBreakpoint } = Grid;

export default function AddZoneForm() {
    const dispatch = useDispatch();
    const mapRef:any = useRef();
    const polygonRefs:any = useRef([]);
    const activePolygonIndex:any = useRef();
    const autocompleteRef:any = useRef();
    const screens = useBreakpoint();

    const history = useHistory();
    const onClickBack = useCallback(() => {
        history.goBack();
    }, [history]);

    // Determine screen size
    const isMobile = !screens.md;
    const isTablet = screens.md && !screens.lg;
    const isDesktop = screens.lg;

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: MAP_API_KEY,
        libraries
    });
    const placesReady = usePlacesLibrary(isLoaded);
    const [isDrawing, setIsDrawing] = useState(false);
    const [draftPath, setDraftPath] = useState<{ lat: number; lng: number }[]>([]);

    const [polygons, setPolygons] = useState<{ lat: number; lng: number }[][]>([]);

    const defaultCenter:any = {
        lat: 31.582045,
        lng: 74.329376,
    }
    const [center] = useState(defaultCenter);

    // Add effect to customize map controls after map loads
    useEffect(() => {
        if (isLoaded && mapRef.current && isMobile) {
            // Add custom styles for mobile controls
            const style = document.createElement('style');
            style.innerHTML = `
                .gm-style .gmnoprint,
                .gm-style .gm-control-active {
                    transform: scale(1.2);
                    transform-origin: top left;
                }
                .gm-style-mtc > div,
                .gm-svpc,
                .gm-control-active {
                    min-width: 44px !important;
                    min-height: 44px !important;
                    padding: 8px !important;
                }
                .gm-style .gm-style-mtc button,
                .gm-style .gm-control-active button {
                    font-size: 16px !important;
                    padding: 8px 12px !important;
                }
            `;
            document.head.appendChild(style);
            
            return () => {
                document.head.removeChild(style);
            };
        }
    }, [isLoaded, isMobile]);

    // Responsive container style
    const getContainerStyle = () => {
        if (isMobile) {
            return {
                width: '100%',
                height: '350px', // Increased for better visibility
            };
        }
        if (isTablet) {
            return {
                width: '100%',
                height: '400px',
            };
        }
        return {
            width: '100%',
            height: '450px',
        };
    };

    const containerStyle = getContainerStyle();

    // Responsive autocomplete style
    const getAutocompleteStyle = () => {
        const baseStyle = {
            boxSizing: 'border-box' as const,
            border: '1px solid transparent',
            padding: '0 12px',
            borderRadius: '3px',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
            fontSize: '14px',
            outline: 'none',
            textOverflow: 'ellipses' as const,
            position: 'absolute' as const,
        };

        if (isMobile) {
            return {
                ...baseStyle,
                width: 'calc(100% - 20px)',
                height: '40px',
                fontSize: '14px',
                left: '10px',
                top: '60px', // Moved down to avoid overlap with drawing controls
                marginLeft: '0',
            };
        }
        if (isTablet) {
            return {
                ...baseStyle,
                width: '200px',
                height: '38px',
                fontSize: '13px',
                right: '10px',
                top: '11px',
                marginLeft: '-100px',
            };
        }
        return {
            ...baseStyle,
            width: '240px',
            height: '38px',
            right: '8%',
            top: '11px',
            marginLeft: '-120px',
        };
    };

    const autocompleteStyle = getAutocompleteStyle();

    // Responsive delete icon style
    const getDeleteIconStyle = () => {
        const baseStyle = {
            cursor: 'pointer',
            backgroundImage: `url(${deleteIcon})`,
            backgroundColor: '#fff',
            position: 'absolute' as const,
            zIndex: 1,
            borderRadius: '2px',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
        };

        if (isMobile) {
            return {
                ...baseStyle,
                top: '10px',
                left: 'calc(50% - 20px)',
                height: '40px',
                width: '40px',
                backgroundSize: '24px 24px',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            };
        }
        if (isTablet) {
            return {
                ...baseStyle,
                top: '2px',
                left: '50%',
                height: '28px',
                width: '28px',
                marginTop: '3px',
            };
        }
        return {
            ...baseStyle,
            top: '2px',
            left: '52%',
            height: '24px',
            width: '24px',
            marginTop: '3px',
        };
    };

    const deleteIconStyle = getDeleteIconStyle();

    const polygonOptions = {
        fillOpacity: 0.3,
        fillColor: '#ff0000',
        strokeColor: '#ff0000',
        strokeWeight: 2,
        draggable: true,
        editable: true
    }

    const onLoadMap = (map: any) => {
        mapRef.current = map;
        
        // Customize map controls for mobile
        if (isMobile) {
            map.setOptions({
                zoomControlOptions: {
                    position: window.google.maps.ControlPosition.RIGHT_BOTTOM
                },
                streetViewControlOptions: {
                    position: window.google.maps.ControlPosition.RIGHT_BOTTOM
                },
                fullscreenControlOptions: {
                    position: window.google.maps.ControlPosition.RIGHT_TOP
                }
            });
        }
    }

    const onLoadPolygon = (polygon:any, index:any) => {
        polygonRefs.current[index] = polygon;
    }

    const onClickPolygon = (index:any) => {
        activePolygonIndex.current = index;
    }

    const onLoadAutocomplete = (autocomplete:any) => {
        autocompleteRef.current = autocomplete;
    }

    const onPlaceChanged = () => {
        const { geometry } = autocompleteRef.current.getPlace();
        const bounds = new window.google.maps.LatLngBounds();
        if (geometry.viewport) {
            bounds.union(geometry.viewport);
        } else {
            bounds.extend(geometry.location);
        }
        mapRef.current.fitBounds(bounds);
    }

    const onMapClick = (event: google.maps.MapMouseEvent) => {
        if (!isDrawing || !event.latLng) {
            return;
        }
        const nextPoint = { lat: event.latLng.lat(), lng: event.latLng.lng() };
        setDraftPath((path) => [...path, nextPoint]);
    }

    const onMoveDraftPoint = (index: number, point: { lat: number; lng: number }) => {
        setDraftPath((path) => path.map((item, itemIndex) => itemIndex === index ? point : item));
    }

    const finishDraft = () => {
        if (draftPath.length < 3) {
            window.alert("Add at least 3 points to create a zone.");
            return;
        }
        const startPoint = draftPath[0];
        setPolygons([[...draftPath, startPoint]]);
        setDraftPath([]);
        setIsDrawing(false);
    }

    const onDeleteDrawing = () => {
        const filtered = polygons.filter((polygon, index) => index !== activePolygonIndex.current)
        setPolygons(filtered)
    }

    const onEditPolygon = (index: number) => {
        const polygonRef = polygonRefs.current[index];
        if (polygonRef) {
            const coordinates = polygonRef.getPath()
                .getArray()
                .map((latLng: { lat: () => any; lng: () => any; }) => ({ lat: latLng.lat(), lng: latLng.lng() }));
            if (coordinates.length < 3) {
                return;
            }

            const allPolygons = [...polygons];
            allPolygons[index] = coordinates;
            setPolygons(allPolygons)
            console.log("Edit Cordinates : ", coordinates)
        }
    }

    const addZone = () => {
        const drawnPath = draftPath.length >= 3
            ? [...draftPath, draftPath[0]]
            : polygons.find((polygon) => polygon.length >= 3);
        if (!drawnPath) {
            window.alert("Please draw polygon first!");
            return;
        }
        if (draftPath.length >= 3) {
            setPolygons([drawnPath]);
            setDraftPath([]);
            setIsDrawing(false);
        }
        dispatch(setCoordinates(drawnPath as []))
        dispatch(setModalName("add_zone_modal"));
    }

    return (
        isLoaded
            ?
            <div className="add-zone-form-container">
                <section className="__table-container">
                    <div className="spacer"></div>
                    <Breadcrumb className="vehicle-route" separator={breadcrumbIcon}>
                        <Breadcrumb.Item>
                            <Button type="link" className="vehicles-link" onClick={onClickBack}>
                                <span className="breadcrumb-text">Zone</span>
                            </Button>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <span className="breadcrumb-text">Add New Zone</span>
                        </Breadcrumb.Item>
                    </Breadcrumb>
                </section>
                
                <div className="instructions-container">
                    <p className="main-instruction">
                        Please draw a polyline to create a geozone and then click on 'Save' button.
                    </p>
                    {!isMobile && (
                        <>
                            <div className="instruction-item">
                                <span className="icon-wrapper">
                                    <div className="icon-container">
                                        <img
                                            alt="Draw polygon"
                                            src="https://maps.gstatic.com/mapfiles/drawing.png"
                                            draggable="false"
                                            className="drawing-icon polygon-icon"
                                        />
                                    </div>
                                </span>
                                <span className="instruction-text">
                                    Click this icon on the map. Each click adds a red corner and connects it to the previous one. Drag a corner to move it, then click the first corner or Done.
                                </span>
                            </div>
                            <div className="instruction-item">
                                <span className="icon-wrapper">
                                    <div className="icon-container">
                                        <img
                                            alt="Move map"
                                            src="https://maps.gstatic.com/mapfiles/drawing.png"
                                            draggable="false"
                                            className="drawing-icon hand-icon"
                                        />
                                    </div>
                                </span>
                                <span className="instruction-text">
                                    Click this icon on the map to move around the map.
                                </span>
                            </div>
                        </>
                    )}
                </div>
                
                <div className="map-container">
                    {
                        polygons.some((polygon) => polygon.length > 0) &&
                        <div
                            onClick={onDeleteDrawing}
                            title='Delete shape'
                            style={deleteIconStyle}
                            className="delete-icon"
                        >
                        </div>
                    }
                    <div
                        style={{
                            position: 'absolute',
                            zIndex: 2,
                            top: isMobile ? 10 : 8,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            background: '#fff',
                            borderRadius: 2,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                        }}
                    >
                        <button
                            type="button"
                            title="Draw a zone"
                            onClick={() => {
                                setDraftPath([]);
                                setIsDrawing(true);
                            }}
                            style={{ width: 32, height: 32, border: 0, background: isDrawing ? '#e6f4ff' : '#fff', cursor: 'pointer' }}
                        >
                            <span style={{ display: 'inline-block', width: 16, height: 16, overflow: 'hidden', position: 'relative' }}>
                                <img alt="" src="https://maps.gstatic.com/mapfiles/drawing.png" draggable={false} style={{ position: 'absolute', left: 0, top: -64, width: 16, height: 192 }} />
                            </span>
                        </button>
                        <button
                            type="button"
                            title="Move the map"
                            onClick={() => setIsDrawing(false)}
                            style={{ width: 32, height: 32, border: 0, background: !isDrawing ? '#e6f4ff' : '#fff', cursor: 'pointer' }}
                        >
                            <span style={{ display: 'inline-block', width: 16, height: 16, overflow: 'hidden', position: 'relative' }}>
                                <img alt="" src="https://maps.gstatic.com/mapfiles/drawing.png" draggable={false} style={{ position: 'absolute', left: 0, top: -144, width: 16, height: 192 }} />
                            </span>
                        </button>
                        {isDrawing && draftPath.length >= 3 && (
                            <button
                                type="button"
                                onClick={finishDraft}
                                style={{ border: 0, background: '#fff', cursor: 'pointer', padding: '0 8px', fontWeight: 600 }}
                            >
                                Done
                            </button>
                        )}
                    </div>
                    <GoogleMap
                        zoom={isMobile ? 13 : 15}
                        center={center}
                        onLoad={onLoadMap}
                        onClick={onMapClick}
                        mapContainerStyle={containerStyle}
                        options={{
                            gestureHandling: isMobile ? 'greedy' : 'auto',
                            zoomControl: true,
                            mapTypeControl: false,
                            scaleControl: false,
                            streetViewControl: !isMobile,
                            rotateControl: false,
                            fullscreenControl: !isMobile,
                            disableDoubleClickZoom: isDrawing,
                        }}
                    >
                        {
                            polygons.filter((iterator) => iterator.length >= 3).map((iterator, index) => (
                                <Polygon
                                    key={index}
                                    onLoad={(event) => onLoadPolygon(event, index)}
                                    onMouseDown={() => onClickPolygon(index)}
                                    onMouseUp={() => onEditPolygon(index)}
                                    onDragEnd={() => onEditPolygon(index)}
                                    options={polygonOptions}
                                    paths={iterator}
                                    draggable
                                    editable
                                />
                            ))
                        }
                        <DraftZonePath
                            path={draftPath}
                            onMovePoint={onMoveDraftPoint}
                            onClose={finishDraft}
                        />
                        {placesReady && (
                        <Autocomplete
                            onLoad={onLoadAutocomplete}
                            onPlaceChanged={onPlaceChanged}
                        >
                            <input
                                type='text'
                                placeholder={isMobile ? 'Search location...' : 'Search Location'}
                                style={autocompleteStyle}
                                className="search-input"
                            />
                        </Autocomplete>
                        )}
                    </GoogleMap>
                </div>
                
                <div className="button-container">
                    <Button
                        type="primary"
                        onClick={addZone}
                        className="save-button"
                        block={isMobile}
                    >
                        <span style={{ fontSize: isMobile ? '14px' : '14px', fontWeight: 600 }}>
                            Save Zone
                        </span>
                    </Button>
                </div>
            </div>
            :
            <div className="loading-container">
                <Spin size="large" />
            </div>
    );
}